import { makeStyles, typographyStyles } from '@fluentui/react-components';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';
import InProgressOperationMessage from '../../common/in-progress-operation-message';
import { DevBoxViewModel } from '../models';
import { DevBoxCardInfoBannerMessageKind } from './models';
import { getNumberDaysUntilScheduledDelete } from './selectors';

interface DevBoxBannerInfoMessageProps {
    kind: DevBoxCardInfoBannerMessageKind;
    ariaLabel?: string;
    devBox: DevBoxViewModel;
}

/**
 * Styles
 */

const boldTextStyles = makeStyles({
    root: typographyStyles.caption1Strong,
});

/**
 * END Styles
 */

export const DevBoxBannerInfoMessage: React.FC<DevBoxBannerInfoMessageProps> = (
    props: DevBoxBannerInfoMessageProps
) => {
    const { kind, ariaLabel, devBox } = props;

    const { pool, resource, devBoxActionState } = devBox;

    const useBoldTextStyles = boldTextStyles();

    const offloadMessageValues = React.useMemo(
        () => ({
            daysUntilDeletion: getNumberDaysUntilScheduledDelete(devBoxActionState),
        }),
        [devBoxActionState]
    );

    const regionMessageValues = React.useMemo(
        () => ({
            region: pool?.location,
        }),
        [pool]
    );

    switch (kind) {
        case DevBoxCardInfoBannerMessageKind.MoveOperationInProgressMessage:
            return (
                <InProgressOperationMessage ariaLabel={ariaLabel}>
                    <div className={useBoldTextStyles.root}>
                        {pool?.location !== resource.location ? (
                            <FormattedMessage
                                id="DevBoxCard_MoveOperationInfo_WithRegion_Text"
                                defaultMessage="Moving to {region}"
                                description="In progress notification for move operation"
                                values={regionMessageValues}
                            />
                        ) : (
                            <FormattedMessage
                                id="DevBoxCard_MoveOperationInfo_WithoutRegion_Text"
                                defaultMessage="Updating network resources"
                                description="In progress notification for move operation"
                            />
                        )}
                    </div>
                    <FormattedMessage
                        id="DevBoxCard_MoveOperationInfo_Text"
                        defaultMessage="Your devbox will be unavailable until this admin action is complete."
                        description="In progress notification for move operation"
                    />
                </InProgressOperationMessage>
            );
        case DevBoxCardInfoBannerMessageKind.RepairOperationInProgressMessage:
            return (
                <InProgressOperationMessage ariaLabel={ariaLabel}>
                    <FormattedMessage
                        id="DevBoxCard_RepairOperationInfo_Text"
                        defaultMessage="Troubleshooting connection. This takes 20 minutes on average, but may take up to 40 minutes."
                        description="In progress notification for repair operation"
                    />
                </InProgressOperationMessage>
            );
        case DevBoxCardInfoBannerMessageKind.TakeSnapshotOperationInProgressMessage:
            return (
                <InProgressOperationMessage ariaLabel={ariaLabel}>
                    <FormattedMessage
                        id="DevBoxCard_TakeSnapshotOperationInfo_Text"
                        defaultMessage="Taking snapshot"
                        description="In progress notification for taking a snapshot operation"
                    />
                </InProgressOperationMessage>
            );
        case DevBoxCardInfoBannerMessageKind.RestoreOperationInProgressMessage:
            return (
                <InProgressOperationMessage ariaLabel={ariaLabel}>
                    <FormattedMessage
                        id="DevBoxCard_RestoreOperationInfo_Text"
                        defaultMessage="Restoring dev box"
                        description="In progress notification for restore operation"
                    />
                </InProgressOperationMessage>
            );
        case DevBoxCardInfoBannerMessageKind.NoIssuesDetectedMessage:
            return (
                <FormattedMessage
                    id="DevBoxCard_RepairOperationNoIssuesDetected_Text"
                    defaultMessage="No issues detected."
                    description="No issues detected notification after repair operation"
                />
            );
        case DevBoxCardInfoBannerMessageKind.CustomizeOperationInProgressMessage:
            return (
                <InProgressOperationMessage ariaLabel={ariaLabel}>
                    <FormattedMessage
                        id="DevBoxCard_CustomizeOperationInfo_Text"
                        defaultMessage="Creating a Dev Box with your customizations. This may take a while."
                        description="In progress notification for customization operation"
                    />
                </InProgressOperationMessage>
            );
        case DevBoxCardInfoBannerMessageKind.WaitingForUserSessionMessage:
            return (
                <FormattedMessage
                    id="DevBoxCard_CustomizeWaitingForUserSessionInfo_Text"
                    defaultMessage="Login to finish setting up this dev box."
                    description="Waiting notification for customization operation requiring user session"
                />
            );
        case DevBoxCardInfoBannerMessageKind.OffloadDevBoxMessage:
            return (
                <>
                    <div className={useBoldTextStyles.root}>
                        {!!offloadMessageValues.daysUntilDeletion && offloadMessageValues.daysUntilDeletion > 0 ? (
                            <FormattedMessage
                                id="DevBoxCard_OffloadDevBoxMessage_Text"
                                defaultMessage="Deletion pending in {daysUntilDeletion} {daysUntilDeletion, plural, one {day} other {days}}"
                                description="Notification for devbox which has pending deletion"
                                values={offloadMessageValues}
                            />
                        ) : (
                            <FormattedMessage
                                id="DevBoxCard_OffloadDevBoxMessage_UndefinedDays_Text"
                                defaultMessage="Deletion pending"
                                description="Notification for devbox which the pending deletion"
                            />
                        )}
                    </div>
                    <FormattedMessage
                        id="DevBoxCard_OffloadDevBoxMessage_AdditionalText"
                        defaultMessage="Your administrator has set this dev box to be deleted. Please contact them if this dev box is still needed."
                        description="Additional details for the pending deletion notification"
                    />
                </>
            );
        default:
            return <></>;
    }
};

export default DevBoxBannerInfoMessage;
